import { useEffect, useState, type FormEvent, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import { longDate } from "../../lib/date";
import { useAuth } from "../../auth/AuthContext";
import type {
  DeletionRequest,
  ExitRequestType,
  ParticipantProfile,
  QuestionnaireOut,
  WithdrawalReason,
} from "../../api/types";
import { PasswordField } from "../PasswordField";
import { TabPanel } from "../Tabs";
import { CONDITIONS, ETHNICITIES, GENDERS, RACES, SMOKING } from "./OneTimeQuestionnaire";

const WITHDRAWAL_REASONS: readonly [WithdrawalReason, string][] = [
  ["privacy_concerns", "Privacy concerns"],
  ["too_time_consuming", "Too time-consuming"],
  ["technical_issues", "Technical issues"],
  ["no_longer_interested", "No longer interested"],
  ["believe_inaccurate", "I believe my results are inaccurate"],
  ["other", "Other"],
];

// Sex at birth is captured by the one-time questionnaire and is read-only here —
// it is shown with the rest of the personal info but not editable from the dashboard.
const SEX_LABELS: Record<string, string> = {
  female: "Female",
  male: "Male",
  intersex: "Intersex",
};

function lookup(
  options: ReadonlyArray<readonly [string, string]>,
  value: unknown,
): string | null {
  if (typeof value !== "string" || !value) return null;
  return options.find(([v]) => v === value)?.[1] ?? value;
}

interface ConditionRow {
  condition?: unknown;
  age_of_diagnosis?: unknown;
  cancer_type?: unknown;
}

// The one-time questionnaire's demographic answers, mapped back to the labels
// the participant chose from. Unknown raw values render as-is rather than hiding.
function demographicRows(payload: Record<string, unknown>): [string, string][] {
  const rows: [string, string][] = [];

  let gender = lookup(GENDERS, payload.gender);
  if (payload.gender === "other" && typeof payload.gender_other === "string") {
    gender = payload.gender_other;
  }
  if (gender) rows.push(["Gender identity", gender]);

  let race = lookup(RACES, payload.race);
  if (race && typeof payload.race_other === "string" && payload.race_other) {
    race = `${race}: ${payload.race_other}`;
  }
  if (race) rows.push(["Race", race]);

  const ethnicity = lookup(ETHNICITIES, payload.ethnicity);
  if (ethnicity) rows.push(["Ethnicity", ethnicity]);

  const smoking = lookup(SMOKING, payload.smoking);
  if (smoking) rows.push(["Smoking history", smoking]);

  if (payload.alcohol === "yes") {
    const drinks = typeof payload.weekly_drinks === "number" ? payload.weekly_drinks : null;
    rows.push(["Alcohol", drinks != null ? `Yes, ${drinks} drink(s)/week` : "Yes"]);
  } else if (payload.alcohol === "no") {
    rows.push(["Alcohol", "No"]);
  }

  if (payload.cosmetic_procedures === "yes") {
    const detail = typeof payload.cosmetic_detail === "string" ? payload.cosmetic_detail : "";
    rows.push(["Cosmetic procedures", detail ? `Yes\n${detail}` : "Yes"]);
  } else if (payload.cosmetic_procedures === "no") {
    rows.push(["Cosmetic procedures", "No"]);
  }

  if (payload.disclose_conditions === "yes") {
    const list = Array.isArray(payload.conditions) ? (payload.conditions as ConditionRow[]) : [];
    const parts = list.map((c) => {
      let label = lookup(CONDITIONS, c.condition) ?? String(c.condition ?? "");
      if (typeof c.cancer_type === "string" && c.cancer_type) label += ` (${c.cancer_type})`;
      if (typeof c.age_of_diagnosis === "number") label += `, diagnosed at ${c.age_of_diagnosis}`;
      return label;
    });
    // Free-text "other" conditions: the repeatable array, plus the legacy
    // single other_condition/other_condition_age keys older payloads carry.
    const others = Array.isArray(payload.other_conditions)
      ? (payload.other_conditions as ConditionRow[])
      : [];
    for (const o of others) {
      if (typeof o.condition !== "string" || !o.condition) continue;
      let label = o.condition;
      if (typeof o.age_of_diagnosis === "number") label += `, diagnosed at ${o.age_of_diagnosis}`;
      parts.push(label);
    }
    if (typeof payload.other_condition === "string" && payload.other_condition) {
      let label = payload.other_condition;
      if (typeof payload.other_condition_age === "number") {
        label += `, diagnosed at ${payload.other_condition_age}`;
      }
      parts.push(label);
    }
    rows.push(["Medical conditions", parts.length > 0 ? parts.join("\n") : "None reported"]);
  } else if (payload.disclose_conditions === "no") {
    rows.push(["Medical conditions", "Not disclosed"]);
  }

  return rows;
}

// Account tab: everything identity-related is read-only (names are part of the
// consent attestation — the study team corrects them on request); email and
// password change through their own safeguarded flows, which sign the user out
// (the server revokes all sessions, so the user must re-authenticate).
export function AccountTab({
  profile,
  oneTime = null,
  study = null,
}: {
  profile: ParticipantProfile;
  oneTime?: QuestionnaireOut | null;
  study?: { slug: string; name: string } | null;
}): ReactElement {
  const { user, logout } = useAuth();

  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emBusy, setEmBusy] = useState(false);
  const [emError, setEmError] = useState<string | null>(null);

  async function changeEmail(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEmBusy(true);
    setEmError(null);
    try {
      await api<{ detail: string }>("POST", "/auth/email/change", {
        json: { current_password: emailPw, new_email: newEmail },
      });
      // Sessions were revoked server-side; signing out routes us back to /login.
      await logout();
    } catch (err) {
      setEmError(err instanceof ApiError ? err.message : "Could not change your email.");
      setEmBusy(false);
    }
  }

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  async function changePassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPwBusy(true);
    setPwError(null);
    try {
      await api<{ detail: string }>("POST", "/auth/password/change", {
        json: { current_password: curPw, new_password: newPw },
      });
      // Sessions were revoked server-side; signing out routes us back to /login.
      await logout();
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : "Could not change your password.");
      setPwBusy(false);
    }
  }

  // ── Leave the study (withdrawal is the ONLY in-app exit, 2026-08-24 —
  // deletion goes by email to the study team, and an admin records it; the
  // recorded request still shows its status here) ──
  const [deletion, setDeletion] = useState<DeletionRequest | null>(null);
  // Whether the withdrawal reason/note form is open. The union stays for the
  // API types, but only "withdrawal" is ever opened from this UI.
  const [exitForm, setExitForm] = useState<ExitRequestType | null>(null);
  const [reason, setReason] = useState<WithdrawalReason>("privacy_concerns");
  const [reasonNote, setReasonNote] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const latest = await api<DeletionRequest | null>(
          "GET",
          "/participants/me/deletion-request",
        );
        setDeletion(latest ?? null);
      } catch {
        setDeletion(null);
      }
    })();
  }, []);

  async function requestExit(requestType: ExitRequestType): Promise<void> {
    setDelBusy(true);
    setDelError(null);
    // Optimistic: show the pending-review state immediately; roll back to the
    // form (with the error) if the request fails.
    const previous = deletion;
    setDeletion({
      id: 0,
      status: "pending",
      request_type: requestType,
      created_at: new Date().toISOString(),
      resolved_at: null,
    });
    setExitForm(null);
    try {
      const created = await api<DeletionRequest>("POST", "/participants/me/deletion-request", {
        json: { reason, request_type: requestType, ...(reasonNote ? { note: reasonNote } : {}) },
      });
      setDeletion(created);
    } catch (err) {
      setDeletion(previous);
      setExitForm(requestType);
      setDelError(err instanceof ApiError ? err.message : "Could not submit the request.");
    } finally {
      setDelBusy(false);
    }
  }

  return (
    <>
      <TabPanel id="account" title="Account Details">
        <dl className="account-readonly">
          <div>
            <dt>First name</dt>
            <dd>{profile.first_name ?? "Not provided"}</dd>
          </div>
          <div>
            <dt>Last name</dt>
            <dd>{profile.last_name ?? "Not provided"}</dd>
          </div>
          {user?.email == null && user?.study_id ? (
            <div>
              <dt>Study ID</dt>
              <dd>{user.study_id}</dd>
            </div>
          ) : (
            <div>
              <dt>Email</dt>
              <dd>{user?.email ?? profile.email}</dd>
            </div>
          )}
          {study ? (
            <div>
              <dt>Study</dt>
              <dd>{study.name}</dd>
            </div>
          ) : null}
          <div>
            <dt>Member since</dt>
            <dd>{longDate(profile.created_at)}</dd>
          </div>
        </dl>
        <p className="field-hint">
          Your name is part of the study record. Contact the study team to correct it.
        </p>
      </TabPanel>

      <section className="app-card">
        <h2>Demographics</h2>
        <dl className="account-readonly">
          <div>
            <dt>Date of birth</dt>
            <dd>{profile.date_of_birth ?? "Not provided"}</dd>
          </div>
          <div>
            <dt>Sex at birth</dt>
            <dd>
              {profile.sex_at_birth
                ? (SEX_LABELS[profile.sex_at_birth] ?? profile.sex_at_birth)
                : "Not provided"}
            </dd>
          </div>
          {oneTime
            ? demographicRows(oneTime.payload).map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))
            : null}
        </dl>
        {!oneTime ? (
          <p className="muted">
            Complete the initial questionnaire to see the rest of your demographics here.
          </p>
        ) : null}
        <p className="field-hint">
          These details are part of the study record. Contact the study team to correct any of
          them.
        </p>
      </section>

      <section className="app-card">
        {/* Study-ID accounts have no email; the form ADDS one (they keep the
            study ID for login). Retitle so the affordance reads correctly. */}
        <h2>{user?.email == null ? "Add an Email (optional)" : "Change Email"}</h2>
        {user?.email == null ? (
          <p className="muted">
            Adding an email lets you reset your own password. Your study ID keeps working for
            sign-in.
          </p>
        ) : null}
        <form className="q-form" onSubmit={changeEmail}>
          <label className="field">
            <span>New email address</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={emailPw}
              onChange={(e) => setEmailPw(e.target.value)}
            />
          </label>
          <p className="field-hint">
            Changing your email signs you out everywhere; you&apos;ll log back in with the new
            address, and we&apos;ll send it a verification link.
          </p>
          {emError ? <p className="form-error">{emError}</p> : null}
          <button
            className="btn-primary-glow"
            type="submit"
            disabled={emBusy || newEmail.length === 0 || emailPw.length === 0}
          >
            {emBusy ? "Updating…" : "Change email"}
          </button>
        </form>
      </section>

      <section className="app-card">
        <h2>Change Password</h2>
        <form className="q-form" onSubmit={changePassword}>
          <label className="field">
            <span>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
            />
          </label>
          <PasswordField label="New password" value={newPw} onChange={setNewPw} />
          <p className="field-hint">
            Changing your password signs you out everywhere; you'll log back in with the new one.
          </p>
          {pwError ? <p className="form-error">{pwError}</p> : null}
          <button
            className="btn-primary-glow"
            type="submit"
            disabled={pwBusy || curPw.length === 0 || newPw.length < 12}
          >
            {pwBusy ? "Updating…" : "Change password"}
          </button>
        </form>
      </section>

      <section className="app-card">
        <h2>Leave the study</h2>
        {deletion?.status === "pending" ? (
          <p className="muted">
            {deletion.request_type === "deletion"
              ? "Your deletion request is pending review by the study team."
              : "Your withdrawal request is pending review by the study team."}
          </p>
        ) : deletion?.status === "executed" ? (
          <p className="muted">
            {deletion.request_type === "deletion"
              ? "Your data has been permanently deleted."
              : "You have withdrawn from the study. Your data is retained under the consent you signed."}
          </p>
        ) : (
          <>
            {deletion?.status === "denied" ? (
              <p className="muted">
                Your previous exit request was declined. Contact the study team for
                details. You may submit a new request.
              </p>
            ) : null}
            <p className="muted">
              No new information will be collected. Information already collected is
              retained under the consent you signed.
            </p>
            {exitForm === null ? (
              <>
                <button
                  className="btn-ghost"
                  type="button"
                  onClick={() => setExitForm("withdrawal")}
                >
                  Withdraw from the study
                </button>
                <p className="muted">
                  To request permanent deletion of your data, email the study team at{" "}
                  <a href="mailto:bwhfaceage@mgb.org" className="link-plain">
                    bwhfaceage@mgb.org
                  </a>
                  .
                </p>
              </>
            ) : (
              <>
                <label className="field">
                  <span>Why are you leaving the study? (helps us improve the study)</span>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value as WithdrawalReason)}
                  >
                    {WITHDRAWAL_REASONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Anything else you want to share? (optional)</span>
                  <input
                    value={reasonNote}
                    maxLength={500}
                    onChange={(e) => setReasonNote(e.target.value)}
                  />
                </label>
                {delError ? <p className="form-error">{delError}</p> : null}
                <div className="btn-row">
                  <button
                    className="btn-primary-glow"
                    type="button"
                    disabled={delBusy}
                    onClick={() => void requestExit(exitForm)}
                  >
                    {delBusy ? "Submitting…" : "Confirm withdrawal request"}
                  </button>
                  <button className="btn-ghost" type="button" onClick={() => setExitForm(null)}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </>
  );
}
